<template>
  <div class="content">
    <app-spinner v-if="!is_loaded" />
    <template v-else>
      <app-cells>
        <h1 class="title">{{ product.name }}</h1>
        <div class="status-title" :style="{ 'backgroundColor': product.status.color || '#8a8a8a' }">
          {{ product.status.name }}
        </div>
      </app-cells>
      <div class="info-cards">
        <div class="info-cards__card">
          <div class="info-cards__row info-cards__row--dotted">
            <span>Last price:</span> <span>{{ product.price.toLocaleString() }} TSh</span>
          </div>
          <div class="info-cards__row info-cards__row--dotted">
            <span>Recommended balance:</span>
            <span>{{ product.recommended_balance.toLocaleString() }} {{ product.measurement.name }}</span>
          </div>
          <div class="info-cards__row info-cards__row--dotted">
            <span>Current stock:</span>
            <span>{{ new Intl.NumberFormat('ru-RU', {minimumFractionDigits: 1}).format(product.in_stock) }} {{ product.measurement.name }}</span>
          </div>
        </div>
        <div class="info-cards__card">
          <div class="info-cards__row info-cards__row--dotted">
            <span>Purchase count:</span>
            <span>{{ product.total_purchased_count || 0 }} {{ product.measurement.name }}</span>
          </div>
          <div class="info-cards__row info-cards__row--dotted">
            <span>Purchase value:</span>
            <span>{{ product.total_purchased_amount ? product.total_purchased_amount.toLocaleString() : 0 }} TSh</span>
          </div>
        </div>
      </div>
      <div v-if="is_general_manager || is_product_storage_supervisor" class="accordion accordion--940">
        <button @click="show_info = !show_info" class="accordion__header" :class="{ 'accordion__header--show': show_info }">
          <span class="accordion__title">Product information</span>
        </button>
        <transition name="slide-small">
          <div v-if="show_info" class="accordion__body">
            <form class="accordion__form" @submit.prevent="onCheckForm">
              <div class="accordion__row accordion__row--type-300">
                <app-form-group label="Name" label-for="name" required>
                  <app-input
                    v-model="form.name"
                    id="name"
                    placeholder="Product name"
                    name="product_name"
                    :error="$v.form.name.$error"
                  />
                  <template #error>
                    <div v-if="$v.form.name.$dirty && !$v.form.name.required">Required field</div>
                  </template>
                </app-form-group>
                <app-form-group label="Category" required>
                  <app-select
                    v-model="form.category"
                    :options="category_options"
                    :clearable="false"
                    placeholder="Category"
                    :error="$v.form.category.$error"
                  />
                  <template #error>
                    <div v-if="$v.form.category.$dirty && !$v.form.category.required">Required field</div>
                  </template>
                </app-form-group>
                <app-form-group label="Measurement" required>
                  <app-select
                    v-model="form.measurement"
                    :options="measurement_options"
                    :clearable="false"
                    placeholder="Measurement"
                    :error="$v.form.measurement.$error"
                  />
                  <template #error>
                    <div v-if="$v.form.measurement.$dirty && !$v.form.measurement.required">Required field</div>
                  </template>
                </app-form-group>
              </div>
              <div class="accordion__row accordion__row--type-8">
                <app-form-group label="Recomm. balance" label-for="recommended_balance" required>
                  <app-input
                    v-model.number="form.recommended_balance"
                    :error="$v.form.recommended_balance.$error"
                    id="recommended_balance"
                    placeholder="Recomm. balance"
                    type="number"
                    name="recommended_balance"
                  />
                  <template #error>
                    <div v-if="$v.form.recommended_balance.$dirty && !$v.form.recommended_balance.required">Required field</div>
                  </template>
                </app-form-group>
                <app-form-group label="Supplier" required>
                  <app-select
                    v-model="form.suppliers"
                    :options="suppliers_options"
                    :clearable="false"
                    :closeOnSelect="false"
                    placeholder="Supplier"
                    :error="$v.form.suppliers.$error"
                    multiple
                  />
                  <template #error>
                    <div v-if="$v.form.suppliers.$dirty && !$v.form.suppliers.required">Required field</div>
                  </template>
                </app-form-group>
              </div>
              <app-cells :indent="false" position="end">
                <app-button ref="submit_info" type="submit">Save</app-button>
              </app-cells>
            </form>
          </div>
        </transition>
      </div>
      <app-cells>
        <h2 class="title">Balance history</h2>
        <div class="date-picker-wrapper">
          <VueCtkDateTimePicker
              id="date_picker_balance"
              class="custom-date-time-picker custom-date-time-picker--range"
              v-model="balance_dates"
              :custom-shortcuts="custom_shortcuts"
              shortcut="last90Days"
              @is-hidden="onIsHiddenPickerBalance"
              format="YYYY-MM-DD"
              formatted="ll"
              label="Select dates"
              color="#77b017"
              button-color="#77b017"
              overlay
              no-header
              no-label
              locale="en"
              range
              noClearButton
          />
          <div v-if="date_picker_balance_error" class="date-picker-wrapper__error">Choose an end date</div>
        </div>
        <app-select
          @input="onChangeFilter"
          v-model="responsible_filter"
          :options="responsible_center_options"
          placeholder="Responsible center"
          class="select--200"
        />
        <app-select
          @input="onChangeFilter"
          v-if="responsible_filter"
          v-model="department_filter"
          :reduce="item => item.code"
          :options="department_options"
          placeholder="Department"
          class="select--200"
        />
        <app-select
          @search="onEmployeeSearch"
          @input="onChangeFilter"
          v-model="employees_filter"
          :options="employees_options"
          label="full_name"
          placeholder="Responsible"
          class="select--200"
        />
      </app-cells>
      <div class="charts-grid">
        <highcharts :options="balance_options" />
      </div>
      <v-client-table
          :data="table_balance.items"
          :columns="table_balance.columns"
          :options="table_balance.options"
          class="table-default"
      >
        <template #h__created_at><div class="table-default__left">Date</div></template>
        <template #h__event_id><div class="table-default__left">Event</div></template>
        <template #h__responsible.first_name><div class="table-default__left">Responsible</div></template>
        <template #created_at="{ row }">
          <div class="table-default__left">{{ dateTimeFormat(row.created_at) }}</div>
        </template>
        <template #event_id="{ row }">
          <div class="table-default__left">
            <router-link
                v-if="row.event"
                :to="{ name: `product-storage-${row.event.toLowerCase()}-detail`, params: { id: row.event_id } }"
                class="table-default__link-border"
            >
              {{ row.event }} (ID: {{ row.event_id }})
            </router-link>
            <template v-else>{{ row.reason }}</template>
          </div>
        </template>
        <template #responsible_center="{ row }">
          {{ row.responsible_center ? row.responsible_center.name : '-' }}
        </template>
        <template #responsible_center_department="{ row }">
          {{ row.responsible_center_department ? row.responsible_center_department.name : '-' }}
        </template>
        <template #responsible.first_name="{ row }">
          <div class="table-default__left">
            <template v-if="row.responsible">
              <router-link
                  v-if="is_general_manager"
                  :to="{ name: 'employees-profile', params: { id: row.responsible.id } }"
                  class="table-default__link-border"
              >
                {{ row.responsible.full_name }}
              </router-link>
              <div v-else class="table-default__left">{{ row.responsible.full_name }}</div>
            </template>
            <template v-if="row.responsible_crew">
              <router-link
                v-if="is_general_manager"
                :to="{ name: 'climbing-mountain-crew-detail', params: { id: row.responsible_crew.id } }"
                class="table-default__link-border"
              >
                {{ row.responsible_crew.full_name }}
              </router-link>
              <div v-else class="table-default__left">{{ row.responsible_crew.full_name }}</div>
            </template>
          </div>
        </template>
        <template #from_value="{ row }">{{ new Intl.NumberFormat('ru-RU').format( row.from_value) }}</template>
        <template #to_value="{ row }">{{ new Intl.NumberFormat('ru-RU').format( row.to_value) }}</template>
        <template #type="{ row }">
          <span :class="{
            'table-default__text-red': row.type === 'Decrease',
            'table-default__text-green': row.type === 'Increase'
          }">
            {{ row.type }}
          </span>
        </template>
      </v-client-table>
      <app-cells>
        <h2 class="title">Price & Purchase history</h2>
        <div class="date-picker-wrapper">
          <VueCtkDateTimePicker
            id="date_picker_price"
            class="custom-date-time-picker custom-date-time-picker--range"
            v-model="price_dates"
            :custom-shortcuts="custom_shortcuts"
            shortcut="last90Days"
            @is-hidden="onIsHiddenPickerPrice"
            format="YYYY-MM-DD"
            formatted="ll"
            label="Select dates"
            color="#77b017"
            button-color="#77b017"
            overlay
            no-header
            no-label
            locale="en"
            range
            noClearButton
          />
          <div v-if="date_picker_price_error" class="date-picker-wrapper__error">Choose an end date</div>
        </div>
      </app-cells>
      <div class="charts-grid">
        <highcharts :options="price_options" />
      </div>
      <v-client-table
          :data="table_price.items"
          :columns="table_price.columns"
          :options="table_price.options"
          class="table-default"
      >
        <template #h__created_at><div class="table-default__left">Date</div></template>
        <template #h__event_id><div class="table-default__left">Event</div></template>
        <template #h__supplier><div class="table-default__left">Supplier</div></template>
        <template #h__responsible.first_name><div class="table-default__left">Buyer</div></template>
        <template #created_at="{ row }">
          <div class="table-default__left">
            {{ dateTimeFormat(row.created_at) }}
          </div>
        </template>
        <template #event_id="{ row }">
          <div class="table-default__left">
            <router-link
              v-if="row.event"
              :to="{ name: `product-storage-${row.event.toLowerCase()}-detail`, params: { id: row.event_id } }"
              class="table-default__link-border"
            >
              {{ row.event }} (ID: {{ row.event_id }})
            </router-link>
            <template v-else>{{ row.reason }}</template>
          </div>
        </template>
        <template #supplier="{ row }">
          <div class="table-default__left">
            <router-link
              v-if="row.supplier && row.supplier.name"
              :to="{ name: 'supplier-detail', params: { id: row.supplier.id } }"
              class="table-default__link-border"
            >
              {{ row.supplier.name }}
            </router-link>
            <template v-else>-</template>
          </div>
        </template>
        <template #responsible.first_name="{ row }">
          <div class="table-default__left">
            <template v-if="row.responsible">
              <router-link
                  v-if="is_general_manager"
                  :to="{ name: 'employees-profile', params: { id: row.responsible.id } }"
                  class="table-default__link-border"
              >
                {{ row.responsible.full_name }}
              </router-link>
              <div v-else class="table-default__left">{{ row.responsible.full_name }}</div>
            </template>
            <span v-else>-</span>
          </div>
        </template>
        <template #quantity="{ row }">
          <span v-if="row.quantity">
            {{ new Intl.NumberFormat('ru-RU', {minimumFractionDigits: 1}).format(row.quantity) }} {{ row.product.measurement.name }}
          </span>
          <span v-else>-</span>
        </template>
        <template #from_value="{ row }">{{ row.from_value.toLocaleString() }} TSh</template>
        <template #to_value="{ row }">{{ row.to_value.toLocaleString() }} TSh</template>
        <template #type="{ row }">
          <span :class="{
            'table-default__text-red': row.type === 'Increase',
            'table-default__text-green': row.type === 'Decrease'
          }">
            {{ row.type }}
          </span>
        </template>
        <template #percent="{ row }">
          <span :class="{
            'table-default__text-green': row.percent < 0,
            'table-default__text-red': row.percent > 0
          }">
            {{ row.percent > 0 ? '+' : '' }}{{ row.percent }}%
          </span>
        </template>
      </v-client-table>
    </template>
  </div>
</template>

<script>
import {
  geMeasurements,
  getEmployeesListSearch,
  getProductcategory,
  getProductStorageProduct,
  getProductStorageProductBalanceHistory,
  getProductStorageProductPriceHistory,
  getProductStorageResponsibleCenter,
  getSuppliersProduct,
  patchProductStorageProduct,
} from '@/http'
import VueCtkDateTimePicker from 'vue-ctk-date-time-picker'
import 'vue-ctk-date-time-picker/dist/vue-ctk-date-time-picker.css'
import { Chart } from 'highcharts-vue'
import { format, parseISO } from 'date-fns'
import debounce from 'lodash/debounce'
import { required } from 'vuelidate/lib/validators'

export default {
  name: 'ProductStorageProductDetail',
  components: { highcharts: Chart, VueCtkDateTimePicker },
  data() {
    return {
      employees_filter: '',
      employees_options: [],
      temp: null,
      is_loaded: false,
      product: null,
      balance_dates: null,
      price_dates: null,
      custom_shortcuts: [
        { key: 'last30Days', label: 'Last 30 days', value: 30 },
        { key: 'last60Days', label: 'Last 60 days', value: 60 },
        { key: 'last90Days', label: 'Last 90 days', value: 90 },
        { key: 'thisYear', label: 'This year', value: 'year' },
      ],
      date_picker_balance_error: false,
      date_picker_price_error: false,
      balance_options: {
        accessibility: {
          enabled: false,
        },
        CSSObject: {
          fontFamily: 'regular',
          fontSize: '6.2em',
        },
        chart: {
          type: 'spline'
        },
        title: {
          text: ''
        },
        xAxis: {
          categories: [],
        },
        yAxis: {
          title: {
            text: ''
          }
        },
        plotOptions: {
          series: {
            dataLabels: {
              enabled: true,
            }
          }
        },
        series: []
      },
      price_options: {
        accessibility: {
          enabled: false,
        },
        CSSObject: {
          fontFamily: 'regular',
          fontSize: '6.2em',
        },
        chart: {
          type: 'spline'
        },
        title: {
          text: ''
        },
        xAxis: {
          categories: [],
        },
        yAxis: {
          title: {
            text: ''
          }
        },
        plotOptions: {
          series: {
            dataLabels: {
              enabled: true,
            }
          }
        },
        series: []
      },
      table_balance: {
        items: [],
        columns: ['created_at', 'event_id', 'responsible_center', 'responsible_center_department', 'responsible.first_name', 'from_value', 'to_value', 'type'],
        options: {
          headings: {
            'created_at': 'Date',
            'event_id': 'Reason',
            'responsible_center': 'Responsible center',
            'responsible_center_department': 'Department',
            'responsible.first_name': 'Responsible',
            'from_value': 'From',
            'to_value': 'To',
            'type': 'Type'
          },
          pagination: { show: false },
          sortable: [],
          perPage: 1000,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      table_price: {
        items: [],
        columns: ['created_at', 'event_id', 'supplier', 'responsible.first_name', 'quantity', 'from_value', 'to_value', 'type', 'percent'],
        options: {
          headings: {
            'created_at': 'Date',
            'event_id': 'Event',
            'supplier': 'Supplier',
            'responsible.first_name': 'Buyer',
            'quantity': 'Quantity',
            'from_value': 'From',
            'to_value': 'To',
            'type': 'Type',
            'percent': 'Percent'
          },
          pagination: { show: false },
          sortable: [],
          perPage: 1000,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      show_info: false,
      form: {},
      suppliers_options: [],
      measurement_options: [],
      category_options: [],
      responsible_filter: '',
      department_filter: '',
      responsible_center_options: [],
      department_options: [],
    }
  },
  validations: {
    form: {
      name: { required },
      category: { required },
      measurement: { required },
      recommended_balance: { required },
      suppliers: { required },
    }
  },
  computed: {
    is_general_manager() {
      return this.$store.state.userGroups.map(item => item.code).includes('general_manager')
    },
    is_product_storage_supervisor() {
      return this.$store.state.userGroups.some(group => group.code === 'product_storage_supervisor')
    },
  },
  created() {
    document.title = `Product (ID: ${ this.$route.params.id }) | Altezza Board`
    this.getProductInfo()
    getProductcategory().then(res => { this.category_options = res.data })
    geMeasurements().then(res => { this.measurement_options = res.data })
    getSuppliersProduct().then(response => { this.suppliers_options = response.data })
    getProductStorageResponsibleCenter().then(response => { this.responsible_center_options = response.data })
  },
  methods: {
    getProductInfo() {
      getProductStorageProduct(this.$route.params.id).then(res => {
        this.product = res.data
        this.form = {
          name: res.data.name,
          category: res.data.category,
          measurement: res.data.measurement,
          recommended_balance: res.data.recommended_balance,
          suppliers: res.data.suppliers,
        }
        this.is_loaded = true
        this.$nextTick(() => {
          this.fetchItemsBalance(this.balance_dates.start, this.balance_dates.end)
          this.fetchItemsPrice(this.price_dates.start, this.price_dates.end)
        })
      })
    },
    onEmployeeSearch(search, loading) {
      loading(true)
      this.searchEmployee(loading, search, this)
    },
    searchEmployee: debounce((loading, search, vm) => {
      if (!search) {
        loading(false)
        vm.employees_options = []
      } else {
        getEmployeesListSearch({ search, employment_status: 'working' }).then(response => {
          loading(false)
          vm.employees_options = response.data
        })
      }
    }, 350),
    onChangeFilter() {
      if (this.responsible_filter) {
        this.department_options = this.responsible_filter.departments
      } else {
        this.department_filter = ''
      }
      this.fetchItemsBalance()
    },
    dateTimeFormat(date) {
      return date ? format(parseISO(date), 'dd MMM yyyy HH:mm') : '-'
    },
    fetchItemsBalance() {
      getProductStorageProductBalanceHistory(
        this.$route.params.id,
        {
          date_after: this.balance_dates.start,
          date_before: this.balance_dates.end,
          responsible: this.employees_filter?.id,
          responsible_center: this.responsible_filter && this.responsible_filter.code,
          responsible_center_department: this.department_filter,
        }
      )
        .then(res => {
          this.balance_options.xAxis.categories = res.data.graph.categories
          this.balance_options.series = res.data.graph.series
          this.table_balance.items = res.data.table
        })
    },
    fetchItemsPrice(start = '', end = '') {
      getProductStorageProductPriceHistory(this.$route.params.id, { date_after: start, date_before: end })
        .then(res => {
          this.price_options.xAxis.categories = res.data.graph.categories
          this.price_options.series = res.data.graph.series
          this.table_price.items = res.data.table
        })
    },
    onIsHiddenPickerBalance() {
      if (this.balance_dates.hasOwnProperty('start') &&
          this.balance_dates.start &&
          this.balance_dates.hasOwnProperty('end') &&
          this.balance_dates.end) {
        this.date_picker_balance_error = false
        this.fetchItemsBalance(this.balance_dates.start, this.balance_dates.end)
      } else this.date_picker_balance_error = true
    },
    onIsHiddenPickerPrice() {
      if (this.price_dates.hasOwnProperty('start') &&
        this.price_dates.start &&
        this.price_dates.hasOwnProperty('end') &&
        this.price_dates.end) {
        this.date_picker_price_error = false
        this.fetchItemsPrice(this.price_dates.start, this.price_dates.end)
      } else this.date_picker_price_error = true
    },
    onCheckForm() {
      this.$v.form.$touch()
      if (this.$v.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendForm()
      }
    },
    sendForm() {
      this.$refs.submit_info.preload = true
      patchProductStorageProduct(this.$route.params.id, this.normalizeForm())
        .finally(() => { this.$refs.submit_info.preload = false })
        .then(() => {
          this.getProductInfo()
          this.$notify({
            type: 'success',
            title: 'Product edited',
          })
        })
    },
    normalizeForm() {
      const form = {...this.form}

      form.suppliers = form.suppliers.map(item => item.id)
      form.category = form.category.id
      form.measurement = form.measurement.id

      return form
    },
  }
}
</script>
<style lang="sass">
.date-picker-wrapper
  position: relative

.date-picker-wrapper__error
  position: absolute
  bottom: -18px
  color: $color-error
  font-size: 12px

.slide-small-enter-active
  transition-duration: 0.3s
  transition-timing-function: ease-in

.slide-small-leave-active
  transition-duration: 0.3s
  transition-timing-function: cubic-bezier(0, 1, 0.5, 1)

.slide-small-enter-to, .slide-small-leave
  max-height: 147px
  overflow: hidden

.slide-small-enter, .slide-small-leave-to
  overflow: hidden
  max-height: 0
</style>
